import { useState, useEffect, useRef } from 'react';
import { X, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { useForgotCredentials } from '../hooks/useForgotCredentials';

interface ForgotCredentialsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ForgotCredentialsModal({
  isOpen,
  onClose,
}: ForgotCredentialsModalProps) {
  const { sendForgotCredentials, isLoading } = useForgotCredentials();
  const [registration, setRegistration] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setRegistration('');
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  // Fermer la modal avec Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isLoading, onClose]);

  // Fermer en cliquant en dehors
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (
      modalRef.current &&
      !modalRef.current.contains(e.target as Node) &&
      !isLoading
    ) {
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!registration.trim()) {
      setError('Veuillez saisir votre plaque d\'immatriculation');
      return;
    }

    try {
      await sendForgotCredentials(registration.trim());
      setSuccess(true);
      // Auto-close after 5 seconds on success
      setTimeout(() => {
        onClose();
      }, 5000);
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-md tw-mx-4"
      >
        {/* Header */}
        <div
          className="tw-px-6 tw-py-4 tw-rounded-t-lg"
          style={{
            background: 'linear-gradient(90deg, #575FA7 0%, #8074AF 100%)',
          }}
        >
          <div className="tw-flex tw-items-center tw-justify-between">
            <h2 className="tw-text-xl tw-font-semibold tw-text-white">
              Identifiants oubliés
            </h2>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="tw-p-2 tw-text-white hover:tw-text-gray-200 disabled:tw-opacity-50"
              aria-label="Fermer"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="tw-p-6">
          {!success ? (
            <>
              <p className="tw-text-gray-700 tw-mb-4">
                Saisissez la plaque d'immatriculation de votre véhicule. Si elle correspond à un contrat actif, vous recevrez un email avec vos identifiants de connexion.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="tw-mb-4">
                  <label
                    htmlFor="registration"
                    className="tw-block tw-text-gray-700 tw-font-medium tw-mb-2"
                  >
                    Plaque d'immatriculation
                  </label>
                  <input
                    type="text"
                    id="registration"
                    value={registration}
                    onChange={(e) => setRegistration(e.target.value.toUpperCase())}
                    placeholder="Ex: AB-123-CD"
                    disabled={isLoading}
                    className="tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-purple-500 disabled:tw-opacity-50 disabled:tw-bg-gray-100"
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="tw-mb-4 tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-flex tw-items-start tw-gap-2">
                    <AlertCircle size={20} className="tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <p className="tw-text-sm tw-text-red-600">{error}</p>
                  </div>
                )}

                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 disabled:tw-opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-white tw-rounded-lg hover:tw-opacity-90 disabled:tw-opacity-50"
                    style={{
                      background: 'linear-gradient(90deg, #575FA7 0%, #8074AF 100%)',
                    }}
                  >
                    {isLoading ? (
                      <>
                        <div className="tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-animate-spin" />
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        <span>Envoyer</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="tw-text-center tw-py-4">
              <div className="tw-flex tw-justify-center tw-mb-4">
                <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-green-100 tw-flex tw-items-center tw-justify-center">
                  <CheckCircle size={32} className="tw-text-green-600" />
                </div>
              </div>
              <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-2">
                Email envoyé !
              </h3>
              <p className="tw-text-gray-700 tw-mb-4">
                Si votre plaque correspond à un contrat actif, vous recevrez un email avec vos identifiants de connexion dans quelques instants.
              </p>
              <p className="tw-text-sm tw-text-gray-500">
                Pensez à vérifier vos spams si vous ne le recevez pas.
              </p>
              <button
                onClick={onClose}
                className="tw-mt-6 tw-px-6 tw-py-2 tw-text-white tw-rounded-lg"
                style={{
                  background: 'linear-gradient(90deg, #575FA7 0%, #8074AF 100%)',
                }}
              >
                Fermer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
